import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2, RotateCcw } from 'lucide-react'
import { documentsApi, type DeletedDocumentFile } from '../../api/documents'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { PageSpinner } from '../../components/ui/Spinner'
import { usePageTitle } from '../../hooks/usePageTitle'
import { formatDate } from '../../lib/utils'
import toast from 'react-hot-toast'

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentRecycleBin() {
  usePageTitle('Document Recycle Bin')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const [hardDeleteTarget, setHardDeleteTarget] = useState<DeletedDocumentFile | null>(null)
  const [emptyBinOpen, setEmptyBinOpen] = useState(false)

  const { data: deleted = [], isLoading } = useQuery({
    queryKey: ['documents-deleted', companyId],
    queryFn: () => documentsApi.listDeleted(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['documents-deleted', companyId] })

  const restoreMutation = useMutation({
    mutationFn: (id: string) => documentsApi.restore(companyId!, id),
    onSuccess: () => { toast.success('Document restored'); invalidate() },
    onError: () => toast.error('Failed to restore document'),
  })

  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => documentsApi.hardDelete(companyId!, id),
    onSuccess: () => { toast.success('Document permanently deleted'); invalidate() },
    onError: () => toast.error('Failed to delete document'),
  })

  const emptyBinMutation = useMutation({
    mutationFn: () => documentsApi.emptyBin(companyId!),
    onSuccess: () => { toast.success('Recycle bin emptied'); invalidate() },
    onError: () => toast.error('Failed to empty recycle bin'),
  })

  if (isLoading) return <PageSpinner />

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Document Recycle Bin</h2>
          <p className="text-sm text-gray-500 mt-0.5">Files deleted from leads. Restore or permanently delete.</p>
        </div>
        {deleted.length > 0 && (
          <button
            onClick={() => setEmptyBinOpen(true)}
            className="btn btn-sm bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Empty Recycle Bin ({deleted.length})
          </button>
        )}
      </div>

      {deleted.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-16">Recycle bin is empty</p>
      )}

      {deleted.length > 0 && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">File</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Lead</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Deleted by</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Deleted at</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Size</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {deleted.map((doc: DeletedDocumentFile) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 truncate max-w-[200px]">{doc.originalName}</p>
                    {doc.label && <p className="text-xs text-gray-400 truncate">{doc.label}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/company/${companyId}/leads/${doc.leadId}`}
                      className="text-primary-600 hover:underline text-xs"
                    >
                      View lead
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {doc.deletedByUser
                      ? `${doc.deletedByUser.firstName} ${doc.deletedByUser.lastName}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(doc.deletedAt)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{formatBytes(doc.sizeBytes)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => restoreMutation.mutate(doc.id)}
                        disabled={restoreMutation.isPending}
                        className="btn btn-ghost btn-sm p-1.5 text-gray-400 hover:text-green-600"
                        title="Restore"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setHardDeleteTarget(doc)}
                        className="btn btn-ghost btn-sm p-1.5 text-gray-300 hover:text-red-500"
                        title="Permanently delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!hardDeleteTarget}
        onClose={() => setHardDeleteTarget(null)}
        onConfirm={() => { if (hardDeleteTarget) hardDeleteMutation.mutate(hardDeleteTarget.id) }}
        title="Permanently delete file?"
        message={`"${hardDeleteTarget?.originalName}" will be deleted from disk. This cannot be undone.`}
        confirmLabel="Delete Forever"
        isPending={hardDeleteMutation.isPending}
      />

      <ConfirmDialog
        isOpen={emptyBinOpen}
        onClose={() => setEmptyBinOpen(false)}
        onConfirm={() => emptyBinMutation.mutate()}
        title="Empty recycle bin?"
        message={`Permanently delete all ${deleted.length} file${deleted.length !== 1 ? 's' : ''}? This cannot be undone.`}
        confirmLabel="Empty Bin"
        isPending={emptyBinMutation.isPending}
      />
    </div>
  )
}
